import { useEffect, useState, useCallback } from "react";
import { getSettings, updateSettings, webhookStatus } from "@/lib/api";
import { toast } from "sonner";
import { Save, ShieldCheck, ExternalLink, RefreshCw, CheckCircle2, XCircle, Copy } from "lucide-react";

export default function Settings() {
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [backendUrl, setBackendUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [checkingWebhook, setCheckingWebhook] = useState(false);

  const refreshWebhookStatus = useCallback(async () => {
    setCheckingWebhook(true);
    try {
      const info = await webhookStatus();
      setWebhookInfo(info);
    } catch (e) {
      /* ignore */
    }
    setCheckingWebhook(false);
  }, []);

  useEffect(() => {
    getSettings().then((s) => {
      setFromName(s.from_name || "");
      setFromEmail(s.from_email || "");
      setReplyTo(s.reply_to || "");
      setBackendUrl(s.backend_url || "");
    });
    refreshWebhookStatus();
  }, [refreshWebhookStatus]);

  const copyWebhookUrl = () => {
    const url = `${backendUrl}/api/webhooks/resend`;
    navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        from_name: fromName,
        from_email: fromEmail,
        reply_to: replyTo,
        backend_url: backendUrl,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  return (
    <div className="p-8 max-w-[900px]" data-testid="settings-page">
      <div className="mb-8">
        <div className="section-label mb-2">Configuration</div>
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
          Settings
        </h1>
        <p className="text-sm text-[#475569] mt-2">Sender identity and deliverability configuration.</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6">
        <div className="section-label mb-4">Sender identity</div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">From name</label>
            <input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              className="field-input"
              placeholder="Marketing Team"
              data-testid="input-settings-from-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">From email</label>
            <input
              type="email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              className="field-input"
              placeholder="hello@yourdomain.com"
              data-testid="input-settings-from-email"
            />
            <div className="text-xs text-[#94A3B8] mt-1.5">
              Must be from a verified domain in Resend.
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Reply-to</label>
          <input
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            className="field-input"
            placeholder="support@yourdomain.com"
            data-testid="input-settings-reply-to"
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
            Tracking base URL <span className="text-xs text-[#94A3B8] font-normal">(for open/click tracking)</span>
          </label>
          <input
            value={backendUrl}
            onChange={(e) => setBackendUrl(e.target.value)}
            className="field-input font-mono text-xs"
            placeholder="https://your-app.preview.emergentagent.com"
            data-testid="input-settings-backend-url"
          />
          <div className="text-xs text-[#94A3B8] mt-1.5">
            Auto-detected from your deployment. Update only if you need a custom domain.
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6" data-testid="webhook-diagnostic-card">
        <div className="flex items-center justify-between mb-4">
          <div className="section-label">Real-time delivery tracking (recommended)</div>
          <button
            onClick={refreshWebhookStatus}
            className="btn-secondary inline-flex items-center gap-1.5 text-xs"
            disabled={checkingWebhook}
            data-testid="btn-refresh-webhook"
          >
            <RefreshCw size={12} className={checkingWebhook ? "animate-spin" : ""} />
            {checkingWebhook ? "Checking…" : "Check status"}
          </button>
        </div>

        {/* Status indicator */}
        {webhookInfo && (
          <div
            className={`rounded-md border p-3 mb-4 flex items-start gap-3 ${
              webhookInfo.connected
                ? "bg-[#ECFDF5] border-[#A7F3D0]"
                : "bg-[#FEF3C7] border-[#FCD34D]"
            }`}
            data-testid="webhook-status"
          >
            {webhookInfo.connected ? (
              <CheckCircle2 size={18} className="text-[#059669] mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle size={18} className="text-[#B45309] mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#0F172A]" data-testid="webhook-status-text">
                {webhookInfo.connected
                  ? `Webhook active · ${webhookInfo.total_events} events received`
                  : "No webhook events received yet"}
              </div>
              {webhookInfo.connected && webhookInfo.by_type && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(webhookInfo.by_type).map(([k, v]) => (
                    <span key={k} className="badge badge-neutral">{k}: {v}</span>
                  ))}
                </div>
              )}
              {!webhookInfo.connected && (
                <div className="text-xs text-[#B45309] mt-1">
                  After you configure the webhook in Resend, send a test campaign and click &ldquo;Check status&rdquo; again.
                </div>
              )}
              {webhookInfo.last_event_at && (
                <div className="text-xs text-[#94A3B8] mt-1.5">
                  Last event: {new Date(webhookInfo.last_event_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-sm text-[#475569] mb-3">
          Add a Resend webhook so we receive live delivered / bounced / opened / clicked events
          from Gmail, Outlook, and other providers. Without this, we only know what Resend
          <em> accepted</em> — not what actually landed in inboxes.
        </p>
        <ol className="text-sm text-[#475569] space-y-1.5 list-decimal pl-5 mb-3">
          <li>Go to <a className="text-[#002FA7] font-medium hover:underline" href="https://resend.com/webhooks" target="_blank" rel="noreferrer">resend.com/webhooks</a></li>
          <li>Click &ldquo;Add Endpoint&rdquo;</li>
          <li>Paste this URL:</li>
        </ol>
        <div className="flex items-center gap-2">
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md px-3 py-2 font-mono text-xs text-[#0F172A] break-all flex-1" data-testid="webhook-url">
            {backendUrl ? `${backendUrl}/api/webhooks/resend` : "(set tracking URL above first, then save)"}
          </div>
          {backendUrl && (
            <button
              onClick={copyWebhookUrl}
              className="btn-secondary inline-flex items-center gap-1.5 text-xs"
              data-testid="btn-copy-webhook-url"
            >
              <Copy size={12} /> Copy
            </button>
          )}
        </div>
        <p className="text-xs text-[#94A3B8] mt-2">
          <strong>Enable ALL of these events</strong>:{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.delivered</code>,{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.bounced</code>,{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.complained</code>,{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.opened</code>,{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.clicked</code>,{" "}
          <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">email.delivery_delayed</code>.
        </p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={16} className="text-[#10B981]" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#0F172A]">Inbox-first deliverability checklist</div>
            <ul className="text-sm text-[#475569] mt-2 space-y-1.5">
              <li>✓ Verify your sending domain in Resend (SPF + DKIM + DMARC records).</li>
              <li>✓ Use a personal-looking &ldquo;From name&rdquo; (not &ldquo;no-reply&rdquo;).</li>
              <li>✓ Keep subject lines under 50 characters and avoid ALL CAPS.</li>
              <li>✓ Include a reply-to and a physical address to comply with CAN-SPAM.</li>
              <li>✓ Warm up your domain by starting with small batches.</li>
            </ul>
            <a
              href="https://resend.com/domains"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] font-medium mt-3 hover:underline"
              data-testid="link-resend-domains"
            >
              Manage domains on Resend <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="btn-save-settings"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
